import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowUpRightIcon } from 'lucide-react';
import { trackEvent } from '../../utils/analytics';
import { VerticalConfig } from './atnaConfig';
import { useLanguage } from '../LanguageContext';
import { resolveAnswer, questionLabel, VerificationScore, VerificationLabel, WhyThis, InformActNote } from './verification';
type AtnaAssistantProps = {
  vertical: VerticalConfig;
  page?: string;
};
export function AtnaAssistant({
  vertical,
  page = 'unknown'
}: AtnaAssistantProps) {
  const {
    lang
  } = useLanguage();
  const isFR = lang === 'FR';
  const [activeQuestion, setActiveQuestion] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);
  const [typed, setTyped] = useState('');
  const [showHint, setShowHint] = useState(false);
  useEffect(() => {}, []);
  const pick = (q: string) => {
    setActiveQuestion(q);
    setExpanded(false);
    trackEvent('atna_question_clicked', {
      question: q,
      page,
      vertical: vertical.tag
    });
  };
  const reset = () => {
    setActiveQuestion(null);
    setExpanded(false);
    setTyped('');
    setShowHint(false);
  };
  const resolved = resolveAnswer(vertical, activeQuestion, isFR);
  return <section className="py-24 px-6 bg-white" id="atna" data-id="element-89">
      <div className="max-w-3xl mx-auto" data-id="element-90">
        {/* Header */}
        <motion.div initial={{
        opacity: 0,
        y: 24
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-80px'
      }} transition={{
        duration: 0.6
      }} className="text-center mb-12" data-id="element-91">
          <div className="font-sans text-xs font-semibold tracking-[0.2em] text-[#4F46E5] uppercase mb-4" data-id="element-92">
            ATNA Assistant
            <span className="ml-2 text-[#5B6478] normal-case tracking-normal font-normal" data-id="element-93">
              {isFR ? '· Propulsé par ATNA' : '· Powered by ATNA'}
            </span>
          </div>
          <h2 className="font-serif text-3xl md:text-4xl text-[#121212] leading-snug" data-id="element-94">
            {isFR ? 'Une intelligence vérifiée pour les collectivités.' : 'Verified intelligence for communities.'}
          </h2>
        </motion.div>

        {/* Panel */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-60px'
      }} transition={{
        duration: 0.6,
        delay: 0.1
      }} className="border border-[#E4E6EC] bg-white" data-id="element-95">
          <div className="flex items-center justify-between px-6 py-3 border-b border-[#E4E6EC] bg-[#FAFAFB]" data-id="element-96">
            <div className="flex items-center gap-2" data-id="element-97">
              <span className="w-2 h-2 bg-[#4F46E5]" aria-hidden="true" data-id="element-98" />
              <span className="font-sans text-xs font-semibold tracking-wide text-[#121212]/70 uppercase" data-id="element-99">
                ATNA Assistant
              </span>
            </div>
            <span className="font-sans text-[11px] text-[#5B6478]" data-id="element-100">
              {isFR ? 'Démonstration · valeurs illustratives' : 'Demonstration · illustrative values'}
            </span>
          </div>

          <div className="p-6 md:p-8" data-id="element-101">
            {!activeQuestion ? (/* IDLE — answer-first entry: questions + input */
          <div data-id="element-102">
                <p className="font-sans text-base text-[#5B6478] mb-6" data-id="element-103">
                  {isFR ? 'Posez une question sur votre municipalité.' : 'Ask anything about your municipality.'}
                </p>
                <div className="font-sans text-xs font-semibold tracking-wider text-[#5B6478] uppercase mb-4" data-id="element-104">
                  {isFR ? 'Questions suggérées' : 'Suggested questions'}
                </div>
                <div className="flex flex-col gap-3 mb-8" data-id="element-105">
                  {vertical.questions.map(q => <button key={q} onClick={() => pick(q)} className="group flex items-center justify-between text-left border border-[#E4E6EC] bg-white rounded-lg px-5 py-4 font-sans text-base text-[#121212] hover:border-[#4F46E5] transition-colors" data-id="element-106">
                      {questionLabel(q, isFR)}
                      <ArrowUpRightIcon className="w-4 h-4 text-[#4F46E5] opacity-0 group-hover:opacity-100 transition-opacity shrink-0 ml-3" data-id="element-107" />
                    </button>)}
                </div>
                {/* Decorative input — guides to suggested questions (demo) */}
                <div className="flex items-center gap-3 border border-[#E4E6EC] px-4 py-3" data-id="element-108">
                  <input value={typed} onChange={e => setTyped(e.target.value)} onKeyDown={e => {
                if (e.key === 'Enter') setShowHint(true);
              }} placeholder={isFR ? 'Demander à ATNA…' : 'Ask ATNA…'} className="flex-1 bg-transparent outline-none font-sans text-base text-[#121212] placeholder:text-[#5B6478]" data-id="element-109" />
                  <button onClick={() => setShowHint(true)} aria-label="Send" className="shrink-0 w-9 h-9 bg-[#4F46E5] text-white flex items-center justify-center hover:bg-[#4F46E5]/90 transition-colors" data-id="element-110">
                    <ArrowUpRightIcon className="w-4 h-4" strokeWidth={2} data-id="element-111" />
                  </button>
                </div>
                {showHint && <p className="mt-2 font-sans text-[11px] text-[#5B6478]" data-id="element-112">
                    {isFR ? 'Cette démonstration répond aux questions suggérées ci-dessus.' : 'This demonstration answers the suggested questions above.'}
                  </p>}
              </div>) : resolved && (/* ANSWER-FIRST */
          <motion.div initial={{
            opacity: 0,
            y: 12
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.4
          }} data-id="element-113">
                  <div className="font-sans text-xs font-semibold tracking-wider text-[#5B6478] uppercase mb-2" data-id="element-114">
                    {isFR ? 'Votre question' : 'You asked'}
                  </div>
                  <p className="font-serif text-lg text-[#121212] mb-8" data-id="element-115">
                    {questionLabel(activeQuestion as string, isFR)}
                  </p>

                  <div className="font-sans text-xs font-semibold tracking-wider text-[#4F46E5] uppercase mb-3" data-id="element-116">
                    {isFR ? 'Recommandation' : 'Recommendation'}
                  </div>
                  <p className="font-serif text-xl md:text-2xl text-[#121212] leading-snug mb-8" data-id="element-117">
                    {resolved.recommendation}
                  </p>

                  <div className="font-sans text-xs font-semibold tracking-wider text-[#5B6478] uppercase mb-4" data-id="element-118">
                    {isFR ? 'Bénéfices attendus' : 'Expected benefits'}
                  </div>
                  <div className="space-y-2.5 mb-8" data-id="element-119">
                    {resolved.results.slice(0, 4).map((r, i) => <div key={i} className="flex items-start gap-3" data-id="element-120">
                        <span className="w-1.5 h-1.5 bg-[#4F46E5] shrink-0 mt-2" aria-hidden="true" data-id="element-121" />
                        <span className="font-sans text-base text-[#121212]" data-id="element-122">{r}</span>
                      </div>)}
                  </div>

                  {/* Compact verification score — optional detail */}
                  <VerificationScore label={resolved.label} isFR={isFR} expanded={expanded} onToggle={() => {
              setExpanded(v => !v);
              if (!expanded) trackEvent('atna_verification_expanded', {
                page,
                vertical: vertical.tag
              });
            }} data-id="element-123" />
                  <AnimatePresence initial={false} data-id="element-124">
                    {expanded && <motion.div initial={{
                opacity: 0,
                height: 0
              }} animate={{
                opacity: 1,
                height: 'auto'
              }} exit={{
                opacity: 0,
                height: 0
              }} transition={{
                duration: 0.35
              }} className="overflow-hidden" data-id="element-125">
                        <div className="pt-4 space-y-4" data-id="element-126">
                          <VerificationLabel label={resolved.label} isFR={isFR} data-id="element-127" />
                          <WhyThis label={resolved.label} isFR={isFR} data-id="element-128" />
                          <InformActNote isFR={isFR} data-id="element-129" />
                        </div>
                      </motion.div>}
                  </AnimatePresence>

                  {/* CTA */}
                  <div className="flex flex-wrap items-center gap-4 pt-8 mt-8 border-t border-[#E4E6EC]" data-id="element-130">
                    <Link to={`/request-demo?source=atna&vertical=${vertical.tag}`} onClick={() => trackEvent('atna_demo_clicked', {
                page,
                vertical: vertical.tag,
                recommended_product: resolved.modules.join(',')
              })} className="inline-flex items-center gap-2 px-6 py-3 bg-[#4F46E5] text-white text-sm font-medium hover:bg-[#4F46E5]/90 transition-colors" data-id="element-131">
                      {isFR ? 'Demander une démo personnalisée' : 'Request a personalized demo'}
                      <ArrowUpRightIcon className="w-4 h-4" strokeWidth={2} data-id="element-132" />
                    </Link>
                    <button onClick={reset} className="font-sans text-sm font-medium text-[#4F46E5] hover:text-[#4F46E5]/80 transition-colors" data-id="element-133">
                      {isFR ? 'Poser une autre question' : 'Ask another question'}
                    </button>
                  </div>
                </motion.div>)}
          </div>
        </motion.div>
      </div>
    </section>;
}